/**
 * InlineError (treatment B) - an error that must NOT take over the screen.
 *
 * Used only in the four situations the spec allows, each because a full-page
 * takeover would destroy something the user still needs:
 *   1. a save / deploy failed and there is an unsaved draft on screen
 *   2. one widget or tab failed inside a page that otherwise loaded
 *   3. a rate limit hit mid-interaction and the prior results are still valid
 *   4. a background action failed and the user has moved on
 *
 * It consumes the SAME `errorStateProps` mapping as <ErrorState>, so the cause,
 * copy, and retry-honesty rules are identical - only the surface differs.
 *
 *   <InlineError error={save.error} noun="policy" onRetry={save.retry} />
 *
 * For a whole list/table/detail failure use <ErrorState> instead: there is no
 * live content to preserve, so a banner floating over an empty region is worse.
 */
import { type ReactElement, type ReactNode } from 'react';
import { Banner } from '../Banner';
import { Button } from '../Button';
import { errorStateProps, type ErrorStateContext } from '../PageState/error-state';

export type InlineErrorProps = ErrorStateContext & {
  error: unknown;
  /** Retry the failed operation. Rendered only when the kind is retryable. */
  onRetry?: () => void;
  /** Handler for the non-retry action (reload-and-compare on a 409, etc.). */
  onAction?: () => void;
  /** Extra trailing content (e.g. a "Discard changes" link). */
  children?: ReactNode;
  className?: string;
};

export function InlineError({
  error,
  onRetry,
  onAction,
  children,
  className,
  ...ctx
}: InlineErrorProps): ReactElement {
  const p = errorStateProps(error, ctx);

  // Same honesty rule as the page treatment: no retry button for a kind where
  // retrying cannot succeed (403 / 404 / 409 / 400).
  const retry =
    p.retryable && onRetry ? (
      <Button size="sm" variant="secondary" onClick={onRetry}>
        {p.retryLabel}
      </Button>
    ) : null;

  const action =
    p.action && onAction ? (
      <Button size="sm" variant="secondary" onClick={onAction}>
        {p.action.label}
      </Button>
    ) : null;

  return (
    <Banner
      tone={p.state === 'denied' ? 'warn' : 'bad'}
      icon={p.icon}
      action={action ?? retry ?? children}
      className={className}
    >
      <span className="font-semibold">{p.headline}</span>
      {p.body ? <span className="font-normal"> {p.body}</span> : null}
    </Banner>
  );
}
